import React, { useEffect, useState } from "react";
import { WizardFormField } from "../machines/immersive-wizard.machine";
import { WizardFormData } from "../types/wizard.types";
import { getAppointmentTypeSuggestionsForGymType } from "../helpers";
import SelectablePills from "../components/SelectablePills";
import ScheduleScreenshotUpload from "../components/ScheduleScreenshotUpload";
import { ClassScheduleExtractorResponse } from "../../../apis/aiApi";
import { useGetClientGymTypeSubType } from "../../../apis/clientApi.hooks";

interface AppointmentTypesStepProps {
  formData: WizardFormData;
  onFormUpdate: (data: Partial<WizardFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

const AppointmentTypesStep: React.FC<AppointmentTypesStepProps> = ({
  formData,
  onFormUpdate,
  onValidationChange,
}) => {
  const [extractedScheduleData, setExtractedScheduleData] = useState<
    (ClassScheduleExtractorResponse & { success: boolean }) | null
  >(null);

  const { data: clientGymTypeSubType, isLoading: isLoadingGymType } =
    useGetClientGymTypeSubType();
  const availableAppointmentTypes = getAppointmentTypeSuggestionsForGymType(
    clientGymTypeSubType?.parentGymType?.label,
    clientGymTypeSubType?.subtype?.label,
  );

  // Validation logic - allow skipping but enable validation if user has made selections
  const hasSelectedAppointments =
    formData[WizardFormField.SelectedAppointmentTypes].length > 0;
  const hasUploadedScreenshot =
    formData[WizardFormField.AppointmentScheduleScreenshot] !== null &&
    formData[WizardFormField.AppointmentScheduleScreenshot] !== "";
  const isValid = hasSelectedAppointments || hasUploadedScreenshot;

  useEffect(() => {
    onValidationChange(isValid);
  }, [isValid, onValidationChange]);

  const handleAppointmentSelectionChange = (selectedAppointments: string[]) => {
    onFormUpdate({
      [WizardFormField.SelectedAppointmentTypes]: selectedAppointments,
    });
  };

  const handleImageUpload = (base64: string, filename: string) => {
    onFormUpdate({
      [WizardFormField.AppointmentScheduleScreenshot]: base64,
      [WizardFormField.AppointmentScheduleScreenshotName]: filename,
    });
  };

  const handleExtractionComplete = (
    extractedData: ClassScheduleExtractorResponse & { success: boolean },
  ) => {
    setExtractedScheduleData(extractedData);

    // Store the extracted appointment schedule data in form data
    onFormUpdate({
      [WizardFormField.ExtractedAppointmentScheduleData]: extractedData,
    });

    // Optionally auto-populate appointment types from extracted data
    if (extractedData.success && extractedData.classes.length > 0) {
      const extractedAppointmentNames = extractedData.classes
        .map((appointment) => appointment.title)
        .filter(
          (title): title is string =>
            typeof title === "string" && title.length > 0,
        );
      const uniqueAppointmentNames = [...new Set(extractedAppointmentNames)];

      // Only auto-populate if user hasn't manually selected appointment types yet
      if (
        formData[WizardFormField.SelectedAppointmentTypes].length === 0 &&
        uniqueAppointmentNames.length > 0
      ) {
        onFormUpdate({
          [WizardFormField.SelectedAppointmentTypes]: uniqueAppointmentNames,
        });
      }
    }
  };

  const currentFile =
    formData[WizardFormField.AppointmentScheduleScreenshot] &&
    formData[WizardFormField.AppointmentScheduleScreenshotName]
      ? {
          base64: formData[WizardFormField.AppointmentScheduleScreenshot],
          name: formData[WizardFormField.AppointmentScheduleScreenshotName],
        }
      : null;

  // Helper function to format time display
  const formatTimeRange = (startTime?: string, endTime?: string) => {
    if (!startTime && !endTime) return "";
    if (startTime && endTime) return `${startTime} - ${endTime}`;
    if (startTime) return `${startTime}`;
    if (endTime) return `until ${endTime}`;
    return "";
  };

  const hasExtractedScheduleData =
    extractedScheduleData &&
    extractedScheduleData.success &&
    extractedScheduleData.classes.length > 0;

  return (
    <div className="tw-space-y-4">
      {/* Appointment Type Pills Section */}
      {isLoadingGymType ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-h-full">
          <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-neutral-800"></div>
        </div>
      ) : (
        <SelectablePills
          title="Select Appointment Types"
          options={availableAppointmentTypes}
          selectedOptions={formData[WizardFormField.SelectedAppointmentTypes]}
          onSelectionChange={handleAppointmentSelectionChange}
        />
      )}

      {/* Divider */}
      <div className="tw-border-t tw-border-neutral-300"></div>

      {/* Screenshot Upload Section */}
      {!hasExtractedScheduleData && (
        <ScheduleScreenshotUpload
          onImageUploaded={handleImageUpload}
          onExtractionComplete={handleExtractionComplete}
          currentFile={currentFile}
        />
      )}

      {/* Extraction Results */}
      {hasExtractedScheduleData && (
        <div className="tw-p-4 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg">
          <h4 className="tw-font-medium tw-text-green-900 tw-mb-3">
            Schedule Analysis Complete ✓
          </h4>
          <p className="tw-text-sm tw-text-green-800 tw-mb-3">
            We found {extractedScheduleData.classes.length} appointments in your
            schedule.
          </p>

          {/* Show extracted appointments preview */}
          <div className="tw-space-y-3">
            <p className="tw-text-sm tw-font-medium tw-text-green-900">
              Detected Appointments:
            </p>
            <div className="tw-space-y-2 tw-max-h-32 tw-overflow-y-auto tw-pr-2">
              {extractedScheduleData.classes.map((appointmentItem, index) => (
                <div
                  key={index}
                  className="tw-p-3 tw-bg-white tw-border tw-border-green-200 tw-rounded tw-text-sm"
                >
                  <div className="tw-font-medium tw-text-green-900">
                    {appointmentItem.title || "Untitled Appointment"}
                  </div>
                  <div className="tw-text-green-700 tw-text-xs tw-mt-1 tw-space-x-3">
                    {appointmentItem.dayOfWeek && (
                      <span className="tw-inline-block">
                        📅 {appointmentItem.dayOfWeek}
                      </span>
                    )}
                    {(appointmentItem.startTime || appointmentItem.endTime) && (
                      <span className="tw-inline-block">
                        🕐{" "}
                        {formatTimeRange(
                          appointmentItem.startTime,
                          appointmentItem.endTime,
                        )}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Auto-population notice */}
          {formData[WizardFormField.SelectedAppointmentTypes].length > 0 && (
            <div className="tw-mt-3 tw-p-2 tw-bg-green-100 tw-rounded tw-text-xs tw-text-green-700">
              💡 Appointment types have been automatically selected based on
              your schedule
            </div>
          )}
        </div>
      )}

      {/* Show message when extraction failed but image was uploaded */}
      {extractedScheduleData &&
        !extractedScheduleData.success &&
        currentFile && (
          <div className="tw-p-4 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg">
            <h4 className="tw-font-medium tw-text-blue-900 tw-mb-2">
              Image Uploaded Successfully
            </h4>
            <p className="tw-text-sm tw-text-blue-800">
              Your appointment schedule image has been saved. Please manually
              select your appointment types above.
            </p>
          </div>
        )}

      <div className="tw-text-sm tw-text-neutral-600 tw-mt-4">
        <p>
          Choose the types of appointments your members can book with your
          staff. These will help create a more personalized experience for your
          members.
        </p>
      </div>
    </div>
  );
};

export default AppointmentTypesStep;
